import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { DateTime } from "luxon";

// mui components
import {
	Box,
	Checkbox,
	FormControlLabel,
	FormGroup,
	Stack,
	Typography,
} from "@mui/material";

// custom components
import CustomLoader from "../../components/loader";
import Body3 from "../../components/typography/body-03";
import Heading2 from "../../components/typography/heading-02";
import Heading3 from "../../components/typography/heading-03";
import MessageCarousel from "../../components/message-carousel";

// custom icons
import LoganLogo from "../../assets/img/login-logo.png";

// api slice
import { useLoginMutation } from "../../features/auth/authApiSlice";
import { useGetOnlySystemMessagesMutation } from "../../features/messages/messagesApiSlice";

// reducer slice
import { setCredentials } from "../../features/auth/authSlice";

// helper utils
import { isValidEmail } from "../../utils/email-validations";

// custom styles
import styles from "./index.module.scss";

const Login = () => {
	const [login, { isLoading }] = useLoginMutation();
	const [getOnlySystemMessages] = useGetOnlySystemMessagesMutation();
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const location = useLocation();

	const [systemMessages, setSystemMessages] = useState([]);

	const [email, setEmail] = useState("");
	const [password, setPassword] = useState("");

	const [error, setError] = useState("");

const handleLoginClick = async (e) => {
	e.preventDefault();

	if (email.trim() === "" || password === "") {
		setError("Email/Password can not be empty.");
		return;
	}

	if (!isValidEmail(email.trim())) {
		setError("Please enter a valid email address.");
		return;
	}

	try {
		const userData = await login({
			username: email.trim(),
			password: password,
		}).unwrap();

		const allowedRoles = ["admin","AM", "AE", "PA" ];
		const userRolesArray = userData?.user_roles?.split(",").map(role => role.trim()) || [];
		const hasAllowedRole = userRolesArray.some(role => allowedRoles.includes(role));

		if (!userData?.login_enabled && !hasAllowedRole) {
			setError("Login has been disabled. Please contact your admin.");
			return;
		}

		console.log("65 login user data:", userData);
		dispatch(setCredentials({ ...userData, username: email.trim() }));

		const from = location.state?.from;
		const redirectTo = from
			? `${from.pathname}${from.search || ""}${from.hash || ""}`
			: "/home";
		navigate(redirectTo, { replace: true, state: null });
	} catch (err) {
		const errorMessage =
			err?.data?.detail ||
			err?.data?.message ||
			(typeof err?.data === 'string' ? err.data : null) ||
			err?.error?.data?.detail ||
			err?.error?.data?.message ||
			"An error occurred during login.";
		
		setError(errorMessage);
	}
};

	useEffect(() => {
		const fetchSystemMessages = async () => {
			try {
				const response = await getOnlySystemMessages().unwrap();
				console.log("Login:fetchSystemMessages:response", response);

				const currentDate = DateTime.now();

				// Filter out expired messages (where end_date is less than current date)
				const activeMessages = (response || []).filter((message) => {
					if (!message.end_date) return true;
					const endDate = DateTime.fromISO(message.end_date);
					return endDate >= currentDate;
				});

				// Sort by start_date proximity to current date (closest first)
				const sortedMessages = activeMessages.sort((a, b) => {
					if (!a.start_date && !b.start_date) return 0;
					if (!a.start_date) return 1;
					if (!b.start_date) return -1;

					const startDateA = DateTime.fromISO(a.start_date);
					const startDateB = DateTime.fromISO(b.start_date);

					const diffA = Math.abs(currentDate.diff(startDateA, "days").days);
					const diffB = Math.abs(currentDate.diff(startDateB, "days").days);

					return diffA - diffB;
				});

				const mappedMessages = sortedMessages.map((message) => {
					return {
						title: message.title,
						description: message.body,
						id: message.id,
					};
				});

				setSystemMessages(mappedMessages);
			} catch (err) {
				console.error("Login:fetchSystemMessages:error", err);
				setSystemMessages([]);
			}
		};

		fetchSystemMessages();
	}, [getOnlySystemMessages]);

	return (
		<Box className={styles.pageContainer}>
			<Box className={styles.bodyContainer}>
				<Box
					className={styles.bodyLeftContainer}
					sx={{
						display: { xs: "none", md: "flex" },
					}}
				>
					<Box className={styles.imageContainer}>
						<img
							src={LoganLogo}
							alt="logan logo"
							styles={{ width: "100%", height: "100%" }}
						/>
					</Box>
					<Stack
						flex={1}
						flexDirection="column"
						className={styles.welcomeContainer}
					>
						<Stack className={styles.contentContainer}>
							<Stack>
								<Heading3
									text="Welcome"
									fontType="bold"
									extraClass={styles.welcomeTitle}
								/>

								<Typography
									component="p"
									sx={{
										fontSize: "1.125rem",
										lineHeight: "2rem",
										fontWeight: 500,
										color: "#fff",
									}}
									className={styles.welcomeDescription}
								>
									Loan management made easy—submit, price, and lock your loans
									with real-time data and effortless control.
								</Typography>
							</Stack>

							<Stack className={styles.messageContainer}>
								<MessageCarousel data={systemMessages} />
							</Stack>
						</Stack>
					</Stack>
					<Stack className={styles.footerContainer} gap={1}>
						<Body3
							text="Please reach out to us if you are experiencing issues logging into the portal or resetting your password at:"
							fontType="semibold"
							extraClass={styles.footerTitle}
						/>
						<Stack flexDirection="row" className={styles.infoContainer}>
							<Body3 text="Email:" extraClass={styles.footerText} />
							<Body3
								text={
									<a
										href="mailto:tpoportalsupport@loganfinance.com"
										rel="noopener noreferrer"
										target="_top"
										className={styles.footerLink}
									>
										tpoportalsupport@loganfinance.com
									</a>
								}
							/>
						</Stack>
						<Stack flexDirection="row" className={styles.infoContainer}>
							<Body3 text="Phone:" extraClass={styles.footerText} />
							<Body3 text="910-302-5924" extraClass={styles.footerLink} />
						</Stack>
					</Stack>
				</Box>

				<Stack className={styles.bodyRightContainer}>
					<form onSubmit={handleLoginClick} className={styles.formContainer}>
						<Box className={styles.formBodyContainer}>
							<Heading2
								text="Login"
								fontType="bold"
								extraClass={styles.loginTitle}
							/>

							<Box className={styles.formFieldsContainer}>
								<Box className={styles.inputFieldContainer}>
									<label htmlFor="email" className={styles.inputLabel}>
										Email
									</label>
									<input
										type="email"
										className={styles.inputField}
										id="email"
										aria-describedby="emailHelp"
										placeholder="Enter your email"
										value={email}
										onChange={(event) => {
											setEmail(event.target.value);
											setError("");
										}}
									/>
								</Box>

								<Box className={styles.inputFieldContainer}>
									<label htmlFor="password" className={styles.inputLabel}>
										Password
									</label>

									<Box className={styles.passwordContainer}>
										<input
											type="password"
											className={styles.passwordField}
											id="password"
											placeholder="Enter your password"
											value={password}
											onChange={(event) => {
												setPassword(event.target.value);
												setError("");
											}}
										/>

										<Link to="/reset-password" className={styles.resetText}>
											Reset password
										</Link>
									</Box>
								</Box>

								<Box className={styles.rememberContainer}>
									<FormGroup>
										<FormControlLabel
											control={
												<Checkbox
													size="small"
													sx={{
														color: "#585756",
														"&.Mui-checked": {
															color: "#49787F",
														},
													}}
												/>
											}
											label={
												<Typography className={styles.rememberText}>
													Remember Password
												</Typography>
											}
										/>
									</FormGroup>
								</Box>
							</Box>

							{error !== "" && <p style={{ color: "#f00" }}>{error}</p>}
							<button
								type="submit"
								onClick={handleLoginClick}
								className={styles.submitBtn}
							>
								{isLoading ? (
									<CustomLoader sx={{ color: "#fff" }} size={20} />
								) : (
									"Login"
								)}
							</button>
						</Box>
					</form>
				</Stack>
			</Box>
		</Box>
	);
};

export default Login;
